/**
 * Preloader Manager
 * Manages different types of loading states and preloader configurations
 */

class PreloaderManager {
  constructor() {
    this.isVisible = false;
    this.currentConfig = null;
    this.loadingSteps = [];
    this.currentStep = 0;
    this.progress = 0;
    this.listeners = [];
  }

  /**
   * Show preloader with configuration
   */
  show(config = {}) {
    this.currentConfig = {
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-home',
      logo: null,
      loadingText: 'Loading...',
      subText: '',
      spinnerType: 'circular',
      backgroundAnimation: 'gradient',
      showProgress: false,
      progress: null,
      steps: [],
      currentStep: 0,
      autoHide: false,
      autoHideDelay: 3000,
      ...config
    };

    this.isVisible = true;
    this.progress = this.currentConfig.progress || 0;
    this.currentStep = this.currentConfig.currentStep || 0;
    this.loadingSteps = this.currentConfig.steps || [];

    this.notifyListeners('show', this.currentConfig);
  }

  /**
   * Hide preloader
   */
  hide() {
    this.isVisible = false;
    this.notifyListeners('hide');
  }

  /**
   * Update loading text
   */
  updateText(text, subText = '') {
    if (this.currentConfig) {
      this.currentConfig.loadingText = text;
      this.currentConfig.subText = subText;
      this.notifyListeners('updateText', { text, subText });
    }
  }

  /**
   * Update progress
   */
  updateProgress(progress) {
    this.progress = Math.min(100, Math.max(0, progress));
    this.notifyListeners('updateProgress', this.progress);
  }

  /**
   * Set loading steps
   */
  setSteps(steps) {
    this.loadingSteps = steps;
    this.currentStep = 0;
    this.notifyListeners('setSteps', { steps, currentStep: 0 });
  }

  /**
   * Move to next step
   */
  nextStep() {
    if (this.currentStep < this.loadingSteps.length - 1) {
      this.currentStep++;
      this.notifyListeners('nextStep', this.currentStep);
    }
  }

  /**
   * Set current step
   */
  setCurrentStep(step) {
    this.currentStep = Math.min(this.loadingSteps.length - 1, Math.max(0, step));
    this.notifyListeners('setCurrentStep', this.currentStep);
  }

  /**
   * Show app initialization preloader
   */
  showAppInit() {
    this.show({
      brandName: 'الوساطة العقارية',
      brandIcon: 'mdi-home',
      loadingText: 'Initializing application...',
      subText: 'Please wait while we set up everything for you',
      spinnerType: 'circular',
      backgroundAnimation: 'gradient',
      steps: [
        'Loading user data...',
        'Initializing components...',
        'Setting up navigation...',
        'Preparing dashboard...',
        'Almost ready...'
      ]
    });
  }

  /**
   * Show data loading preloader
   */
  showDataLoading() {
    this.show({
      brandName: 'الوساطة العقارية',
      loadingText: 'Loading data...',
      subText: 'Fetching the latest information',
      spinnerType: 'dots',
      backgroundAnimation: 'particles',
      showProgress: true,
      progress: 0
    });
  }

  /**
   * Show page transition preloader
   */
  showPageTransition(pageName = '') {
    this.show({
      brandName: 'الوساطة العقارية',
      loadingText: `Loading ${pageName}...`,
      subText: 'Please wait',
      spinnerType: 'wave',
      backgroundAnimation: 'waves',
      autoHide: true,
      autoHideDelay: 2000
    });
  }

  /**
   * Show authentication preloader
   */
  showAuth() {
    this.show({
      brandName: 'الوساطة العقارية',
      loadingText: 'Authenticating...',
      subText: 'Verifying your credentials',
      spinnerType: 'pulse',
      backgroundAnimation: 'gradient',
      steps: [
        'Validating credentials...',
        'Loading user profile...',
        'Setting up session...',
        'Redirecting...'
      ]
    });
  }

  /**
   * Show file upload preloader
   */
  showFileUpload() {
    this.show({
      brandName: 'الوساطة العقارية',
      loadingText: 'Uploading file...',
      subText: 'Please don\'t close this window',
      spinnerType: 'circular',
      backgroundAnimation: 'particles',
      showProgress: true,
      progress: 0
    });
  }

  /**
   * Show theme switching preloader
   */
  showThemeSwitch() {
    this.show({
      brandName: 'الوساطة العقارية',        
      loadingText: 'Switching theme...',
      subText: 'Applying your preferences',
      spinnerType: 'pulse',
      backgroundAnimation: 'gradient',
      autoHide: true,
      autoHideDelay: 1000
    });
  }

  /**
   * Show full page loading with blur
   */
  showFullPageLoading(config = {}) {
    this.show({
      brandName: 'الوساطة العقارية',  
      brandIcon: 'mdi-home',
      loadingText: 'جاري التحميل...',
      subText: 'يرجى الانتظار بينما نقوم بمعالجة طلبك',
      spinnerType: 'circular',
      showProgress: false,
      progress: null,
      steps: [],
      currentStep: 0,
      autoHide: false,
      autoHideDelay: 3000,
      isFullPage: true,
      ...config
    });
  }

  /**
   * Show full page loading for theme switch
   */
  showFullPageThemeSwitch(isDark) {
    this.show({
      brandName: 'Wasata',
      brandIcon: 'mdi-palette',
      loadingText: isDark ? 'جاري التبديل إلى الوضع المظلم...' : 'جاري التبديل إلى الوضع المضيء...',
      subText: 'جاري تطبيق تفضيلات المظهر',
      spinnerType: 'ring',
      showProgress: false,
      progress: null,
      steps: [],
      currentStep: 0,
      autoHide: true,
      autoHideDelay: 1500,
      isFullPage: true,
      isDark: isDark
    });
  }

  /**
   * Show full page loading for app initialization
   */
  showFullPageAppInit() {
    this.show({
      brandName: 'Wasata',
      brandIcon: 'mdi-home',
      loadingText: 'جاري تهيئة التطبيق...',
      subText: 'جاري إعداد كل شيء لك',
      spinnerType: 'circular',
      showProgress: true,
      progress: 0,
      steps: [
        'جاري تحميل بيانات المستخدم...',
        'جاري تهيئة المكونات...',
        'جاري إعداد التنقل...',
        'جاري تحضير لوحة التحكم...',
        'تقريباً جاهز...'
      ],
      currentStep: 0,
      autoHide: false,
      autoHideDelay: 3000,
      isFullPage: true
    });
  }

  /**
   * Add event listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove event listener
   */
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Preloader listener error:', error);
      }
    });
  }

  /**
   * Get current state
   */
  getState() {
    return {
      isVisible: this.isVisible,
      config: this.currentConfig,
      progress: this.progress,
      currentStep: this.currentStep,
      steps: this.loadingSteps
    };
  }

  /**
   * Simulate loading with steps
   */
  async simulateLoading(steps, stepDelay = 1000) {
    this.setSteps(steps);
    
    for (let i = 0; i < steps.length; i++) {
      this.setCurrentStep(i);
      this.updateProgress((i + 1) / steps.length * 100);
      await new Promise(resolve => setTimeout(resolve, stepDelay));
    }
  }

  /**
   * Simulate progress loading
   */
  async simulateProgress(duration = 3000, steps = 20) {
    const stepDuration = duration / steps;
    
    for (let i = 0; i <= steps; i++) {
      this.updateProgress((i / steps) * 100);
      await new Promise(resolve => setTimeout(resolve, stepDuration));
    }
  }
}

// Create singleton instance
const preloaderManager = new PreloaderManager();

export default preloaderManager;
